<template>
    <v-card data-test="custom-invoice-form">
        <v-card-title>{{ readonly ? 'Individuelle Rechnung' : invoice ? 'Rechnung bearbeiten' : 'Individuelle Rechnung erstellen' }}</v-card-title>
        <v-card-text>
            <v-alert v-if="error" type="error" outlined data-test="custom-invoice-error">{{ error }}</v-alert>
            <div data-test="custom-invoice-recipient" class="mb-4">
                <strong>{{ recipient.name }}</strong><br>
                {{ recipient.street }}<br>
                {{ recipient.zip }} {{ recipient.city }}<br>
                Kundennummer: {{ recipient.customer_id }}
            </div>
            <p>Die Rechnungsadresse wird aus den Stammdaten übernommen und auf der Rechnung gespeichert.</p>
            <v-form ref="header" @submit.prevent="next">
                <v-text-field v-model="form.rn_name" name="rn_name" label="RN-Name" :rules="[validators.required]" :readonly="readonly" :disabled="busy" />
                <v-text-field v-model.number="form.invoice_year" name="invoice_year" label="Rechnungsjahr" type="number" min="1000" max="9999" step="1" hint="Dieses Jahr wird für die Rechnungsreferenz verwendet." persistent-hint :rules="[validators.required]" :readonly="readonly" :disabled="busy" />
                <v-text-field v-model="form.invoice_date" name="invoice_date" label="Rechnungsdatum" type="date" :rules="[validators.required]" :readonly="readonly" :disabled="busy" />
                <v-text-field v-model="form.payment_due_date" name="payment_due_date" label="Zahlungsziel" type="date" :rules="[validators.required]" :readonly="readonly" :disabled="busy" />
                <v-textarea v-model="form.internal_note" name="internal_note" label="Interne Notiz (optional)" :readonly="readonly" :disabled="busy" rows="2" />
                <v-btn v-if="!itemsVisible" type="submit" color="primary" data-test="custom-invoice-header-next">Positionen erfassen</v-btn>
            </v-form>
            <v-form v-if="itemsVisible" ref="items" @submit.prevent="save">
                <div v-for="(item, index) in form.items" :key="index" data-test="custom-invoice-item" class="mt-4">
                    <h3>Position {{ index + 1 }}</h3>
                    <v-text-field v-model="item.name" name="item_name" label="Bezeichnung" :rules="[validators.required]" :readonly="readonly" :disabled="busy" />
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="item.net_amount" name="item_net" label="Nettobetrag (€)" :rules="[validators.decimal]" :readonly="readonly" :disabled="busy" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="item.vat_rate" name="item_vat" label="MwSt. (%)" :rules="[validators.decimal]" :readonly="readonly" :disabled="busy" />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="item.service_period_start" name="item_period_start" label="Leistung von (optional)" type="date" :readonly="readonly" :disabled="busy" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="item.service_period_end" name="item_period_end" label="Leistung bis (optional)" type="date" :readonly="readonly" :disabled="busy" />
                        </v-col>
                    </v-row>
                    <p v-if="readonly">MwSt.: {{ money(invoice.items[index].vat_amount) }} · Brutto: {{ money(invoice.items[index].gross_amount) }}</p>
                    <v-btn v-if="!readonly" :disabled="busy || form.items.length === 1" text color="error" data-test="custom-invoice-remove-item" @click="form.items.splice(index, 1)">Position entfernen</v-btn>
                    <v-divider class="mt-3" />
                </div>
                <v-btn v-if="!readonly" :disabled="busy" class="my-4" data-test="custom-invoice-add-item" @click="addItem">Position hinzufügen</v-btn>
            </v-form>
            <div v-if="readonly" data-test="custom-invoice-totals" class="mt-4">
                <p>Referenz: <strong>{{ invoice.reference }}</strong></p>
                <v-simple-table>
                    <thead><tr><th>MwSt.-Satz</th><th>Netto</th><th>MwSt.</th><th>Brutto</th></tr></thead>
                    <tbody>
                        <tr v-for="group in invoice.vat_totals" :key="group.vat_rate">
                            <td>{{ group.vat_rate }} %</td><td>{{ money(group.net_amount) }}</td><td>{{ money(group.vat_amount) }}</td><td>{{ money(group.gross_amount) }}</td>
                        </tr>
                        <tr><th>Gesamt</th><th>{{ money(invoice.net_amount) }}</th><th>{{ money(invoice.vat_amount) }}</th><th>{{ money(invoice.gross_amount) }}</th></tr>
                    </tbody>
                </v-simple-table>
                <h3 class="mt-4">Verlauf</h3>
                <v-simple-table>
                    <thead><tr><th>Aktion</th><th>Zeitpunkt</th><th>Administrator</th></tr></thead>
                    <tbody><tr v-for="(event, index) in invoice.audit" :key="index"><td>{{ auditLabels[event.event] }}</td><td>{{ $formatDate(event.at) }}</td><td>{{ event.by }}</td></tr></tbody>
                </v-simple-table>
            </div>
            <p v-else class="mt-4">Beim Speichern werden die Beträge berechnet. Der Entwurf wird weder übertragen noch versendet.</p>
        </v-card-text>
        <v-card-actions class="justify-end">
            <v-btn :disabled="busy" text data-test="custom-invoice-close" @click="$emit('close')">Schließen</v-btn>
            <v-btn v-if="!readonly && itemsVisible" :loading="busy" :disabled="busy" color="primary" data-test="custom-invoice-save" @click="save">Entwurf speichern</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { validators } from '../../validators';

export default {
    props: {
        invoice: { type: Object, default: null },
        recipient: { type: Object, required: true },
        readonly: Boolean,
        busy: Boolean,
        error: { type: String, default: '' },
    },
    data() {
        return {
            validators,
            auditLabels: { created: 'Erstellt', edited: 'Bearbeitet', reference_created: 'Referenz vergeben', reference_changed: 'Referenz geändert', deleted: 'Gelöscht', transferred: 'An Apobilanz übertragen', retrieved: 'Zurückgeholt', retransferred: 'Erneut übertragen', cancelled: 'Storniert', accounting_exported: 'Für die Buchhaltung exportiert' },
            itemsVisible: Boolean(this.invoice),
            form: this.invoice ? {
                rn_name: this.invoice.rn_name,
                invoice_year: this.invoice.invoice_year,
                invoice_date: this.invoice.invoice_date,
                payment_due_date: this.invoice.payment_due_date,
                internal_note: this.invoice.internal_note,
                items: this.invoice.items.map(item => ({
                    name: item.name,
                    net_amount: item.net_amount,
                    vat_rate: item.vat_rate,
                    service_period_start: item.service_period_start,
                    service_period_end: item.service_period_end,
                })),
            } : {
                rn_name: '', invoice_year: new Date().getFullYear(), invoice_date: '', payment_due_date: '', internal_note: '',
                items: [{ name: '', net_amount: '', vat_rate: '19', service_period_start: '', service_period_end: '' }],
            },
        };
    },
    methods: {
        next() {
            if (this.$refs.header.validate()) this.itemsVisible = true;
        },
        addItem() {
            this.form.items.push({ name: '', net_amount: '', vat_rate: '19', service_period_start: '', service_period_end: '' });
        },
        save() {
            const headerValid = this.$refs.header.validate();
            const itemsValid = this.$refs.items.validate();
            if (headerValid && itemsValid && !this.busy) this.$emit('save', {
                ...this.form,
                items: this.form.items.map(item => ({
                    ...item,
                    net_amount: String(item.net_amount).replace(',', '.'),
                    vat_rate: String(item.vat_rate).replace(',', '.'),
                })),
            });
        },
        money(value) {
            return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
        },
    },
};
</script>
